import { Formik } from 'formik';
import { useCallback, useContext } from 'react';

import { customersApi } from 'src/app/services/Customers/Customers.service';
import {
  type ICustomerLeadProductAddressTabFormValues,
  customerLeadProductContactTabValidationSchema,
  toCustomerLeadProductAddressTabInitialValues,
} from 'src/components/customers/steps/Contact/CustomerLeadStepAddressTab.validation';
import { CustomerLeadStepAddressTabContent } from 'src/components/customers/steps/Contact/CustomerLeadStepAddressTabContent';
import { toApiAddress } from 'src/helpers/toApiAddress';
import { CustomersContext } from 'src/pages/CustomersPage/CustomersPage.context';
import type { ICustomerLeadProduct } from 'src/types/customers/ICustomerLeadProduct';

type CustomerLeadStepContactTabProps = {
  addressFieldName: Extract<keyof ICustomerLeadProduct, 'shippingAddress' | 'billingAddress'>;
  customerProductLeadId: string;

  onSubmit: () => boolean;
};

export function CustomerLeadStepAddressTab({
  addressFieldName,
  customerProductLeadId,
  onSubmit,
}: Readonly<CustomerLeadStepContactTabProps>) {
  const { customerLeadProduct, updateCustomerLeadProduct } = useContext(CustomersContext);

  const [, partialUpdateCustomerProductLead] =
    customersApi.usePartialUpdateCustomerProductLeadMutation(customerProductLeadId);

  const save = useCallback(
    async (newValues: ICustomerLeadProductAddressTabFormValues, submit: boolean) => {
      const updatedCustomerProductLeadData = await partialUpdateCustomerProductLead({
        data: {
          [addressFieldName]: {
            ...toApiAddress(newValues.address),

            submit,
          },
        },
      });

      updateCustomerLeadProduct(updatedCustomerProductLeadData.data);
    },
    [partialUpdateCustomerProductLead, addressFieldName, updateCustomerLeadProduct]
  );

  const handleSubmit = useCallback(
    async (submitValues: ICustomerLeadProductAddressTabFormValues) => {
      await save(submitValues, true);

      return onSubmit();
    },
    [save, onSubmit]
  );

  const handleChange = useCallback(
    (newValues: ICustomerLeadProductAddressTabFormValues) => save(newValues, false),
    [save]
  );

  if (!customerLeadProduct) {
    return null;
  }

  return (
    <Formik<ICustomerLeadProductAddressTabFormValues>
      initialValues={toCustomerLeadProductAddressTabInitialValues(customerLeadProduct, addressFieldName)}
      validationSchema={customerLeadProductContactTabValidationSchema}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {(formikProps) => <CustomerLeadStepAddressTabContent formikProps={formikProps} onChange={handleChange} />}
    </Formik>
  );
}
